import {
  Autocomplete,
  Box,
  TextField,
  TextFieldProps,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";

export interface State {
  fullName: string;
  abbreviation: string;
}
export const StatesList: Array<State> = [
  { fullName: "Alabama", abbreviation: "AL" },
  { fullName: "Alaska", abbreviation: "AK" },
  { fullName: "Arizona", abbreviation: "AZ" },
  { fullName: "Arkansas", abbreviation: "AR" },
  { fullName: "California", abbreviation: "CA" },
  { fullName: "Colorado", abbreviation: "CO" },
  { fullName: "Connecticut", abbreviation: "CT" },
  { fullName: "Delaware", abbreviation: "DE" },
  { fullName: "Florida", abbreviation: "FL" },
  { fullName: "Georgia", abbreviation: "GA" },
  { fullName: "Hawaii", abbreviation: "HI" },
  { fullName: "Idaho", abbreviation: "ID" },
  { fullName: "Illinois", abbreviation: "IL" },
  { fullName: "Indiana", abbreviation: "IN" },
  { fullName: "Iowa", abbreviation: "IA" },
  { fullName: "Kansas", abbreviation: "KS" },
  { fullName: "Kentucky", abbreviation: "KY" },
  { fullName: "Louisiana", abbreviation: "LA" },
  { fullName: "Maine", abbreviation: "ME" },
  { fullName: "Maryland", abbreviation: "MD" },
  { fullName: "Massachusetts", abbreviation: "MA" },
  { fullName: "Michigan", abbreviation: "MI" },
  { fullName: "Minnesota", abbreviation: "MN" },
  { fullName: "Mississippi", abbreviation: "MS" },
  { fullName: "Missouri", abbreviation: "MO" },
  { fullName: "Montana", abbreviation: "MT" },
  { fullName: "Nebraska", abbreviation: "NE" },
  { fullName: "Nevada", abbreviation: "NV" },
  { fullName: "New Hampshire", abbreviation: "NH" },
  { fullName: "New Jersey", abbreviation: "NJ" },
  { fullName: "New Mexico", abbreviation: "NM" },
  { fullName: "New York", abbreviation: "NY" },
  { fullName: "North Carolina", abbreviation: "NC" },
  { fullName: "North Dakota", abbreviation: "ND" },
  { fullName: "Ohio", abbreviation: "OH" },
  { fullName: "Oklahoma", abbreviation: "OK" },
  { fullName: "Oregon", abbreviation: "OR" },
  { fullName: "Pennsylvania", abbreviation: "PA" },
  { fullName: "Rhode Island", abbreviation: "RI" },
  { fullName: "South Carolina", abbreviation: "SC" },
  { fullName: "South Dakota", abbreviation: "SD" },
  { fullName: "Tennessee", abbreviation: "TN" },
  { fullName: "Texas", abbreviation: "TX" },
  { fullName: "Utah", abbreviation: "UT" },
  { fullName: "Vermont", abbreviation: "VT" },
  { fullName: "Virginia", abbreviation: "VA" },
  { fullName: "Washington", abbreviation: "WA" },
  { fullName: "West Virginia", abbreviation: "WV" },
  { fullName: "Wisconsin", abbreviation: "WI" },
  { fullName: "Wyoming", abbreviation: "WY" },
];

interface StateInputProps {
  label?: string;
  value?: State | string;
  textFieldProps?: TextFieldProps;
  onChange?: (state: State | undefined) => void;
}

/**
 *
 * @param label - Input label for element
 * @param value - State object or string value
 * @param textFieldProps - Props for rendering input
 * @param onChange - State object callback when the user selects a state
 * @returns State object or undefined
 */
const StateInput = ({
  label,
  textFieldProps,
  value,
  onChange,
}: StateInputProps) => {
  const findState = (input: string): State | undefined => {
    const lower = input.trim().toLowerCase();
    if (!lower) return undefined;
    let match = StatesList.find((s) => s.abbreviation.toLowerCase() === lower);
    if (match) return match;
    match = StatesList.find((s) => s.fullName.toLowerCase() === lower);
    if (match) return match;
    match = StatesList.find((s) =>
      s.abbreviation.toLowerCase().startsWith(lower),
    );
    if (match) return match;
    match = StatesList.find((s) => s.fullName.toLowerCase().startsWith(lower));
    if (match) return match;
    match = StatesList.find(
      (s) =>
        s.fullName.toLowerCase().includes(lower) ||
        s.abbreviation.toLowerCase().includes(lower),
    );
    return match;
  };

  const [inputValue, setInputValue] = useState("");
  const [selected, setSelected] = useState<State | undefined>(
    typeof value === "string" ? findState(value) : value,
  );

  useEffect(() => {
    const stateObj = typeof value === "string" ? findState(value) : value;
    setSelected(stateObj);
    setInputValue(stateObj ? stateObj.fullName : "");
  }, [value]);

  useEffect(() => {
    if (inputValue && (!selected || selected.fullName !== inputValue)) {
      const match = findState(inputValue);
      if (match) {
        setSelected(match);
        onChange?.(match);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inputValue]);

  const handleSelect = (state: State | null) => {
    setSelected(state || undefined);
    if (state === null) onChange?.(undefined);
    else onChange?.(state);
  };

  const handleBlur = () => {
    if (!selected && inputValue) {
      const match = findState(inputValue);
      if (match) {
        setSelected(match);
        setInputValue(match.fullName);
        onChange?.(match);
      }
    }
  };

  return (
    <Box position="relative">
      {/* This hidden input is here for browser compatibility. When the browser autofills an address,
      it will be entered here and the autocomplete can work with this value then. */}
      <input
        type="text"
        style={{
          position: "absolute",
          opacity: 0,
          pointerEvents: "none",
          height: 0,
          width: 0,
        }}
        tabIndex={-1}
        autoComplete="address-level1"
        value={inputValue}
        onChange={(e) => {
          setInputValue(e.target.value);
        }}
      />
      <Autocomplete
        autoCapitalize="words"
        autoSelect
        options={StatesList}
        value={selected || null}
        inputValue={inputValue}
        onInputChange={(_, val) => setInputValue(val)}
        onChange={(_, st) => handleSelect(st)}
        onBlur={handleBlur}
        renderInput={(props) => (
          <TextField
            {...props}
            {...textFieldProps}
            label={label ?? "State"}
            autoComplete="address-level1"
          />
        )}
        renderOption={({ key, ...props }, option) => (
          <Box component={"li"} key={key} {...props}>
            {<Typography>{option.fullName}</Typography>}
          </Box>
        )}
        getOptionLabel={(option) => option.fullName}
      />
    </Box>
  );
};

export default StateInput;
